<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from './stores'
import { useQuasar } from 'quasar'
import { useResponsive } from '@/composables/useResponsive'

import AppHeaderQuasar from '@/components/layout/AppHeaderQuasar.vue'
import AppFooterQuasar from '@/components/layout/AppFooterQuasar.vue'
import MobileNavigation from '@/components/layout/MobileNavigation.vue'
import PromoHeaderBanner from '@/components/layout/PromoHeaderBanner.vue'

const { isMobile } = useResponsive()
const $q = useQuasar()

const leftDrawerOpen = ref(false)

const promoBanner = ref({
  active: true,
  logoUrl: 'https://cdn.hostinger.com/hostinger-logos/2019/logo-light.svg',
  text: 'Get up to 80% off on Hostinger hosting plans',
  buttonText: 'SHOP NOW',
  link: 'https://hostinger.com/special-offer',
  trackingId: 'hostinger-promo-banner'
})

const isBannerActive = computed(() => promoBanner.value.active)

const route = useRoute()
const router = useRouter()
const isHomePage = computed(() => route.path === '/' || route.path === '/home')
const isAdminRoute = computed(() => route.path.includes('/admin'))

const toggleLeftDrawer = () => {
  leftDrawerOpen.value = !leftDrawerOpen.value
}

watch(() => route.path, () => {
  if (isMobile.value && leftDrawerOpen.value) {
    leftDrawerOpen.value = false
  }
})

router.onError(() => {
  const message = isAdminRoute.value
    ? 'Admin navigation error'
    : 'Navigation error occurred'

  $q.notify({
    type: 'negative',
    message,
    caption: 'Please try refreshing the page',
    position: 'top',
    actions: [
      { label: 'Refresh', color: 'white', handler: () => window.location.reload() }
    ]
  })
})

const authStore = useAuthStore()

// Offline: only show a subtle banner in the UI, no toast spam.
// The notification fires only on actual *transitions* (online->offline or offline->online),
// never on initial page load.
const isOffline = ref(false)
let hasBeenOffline = false

const onOffline = () => {
  isOffline.value = true
  hasBeenOffline = true
  $q.notify({
    message: 'You appear to be offline',
    icon: 'cloud_off',
    color: 'warning',
    position: 'bottom',
    timeout: 3000
  })
}

const onOnline = () => {
  isOffline.value = false
  if (hasBeenOffline) {
    $q.notify({
      message: 'Connection restored',
      icon: 'cloud_done',
      color: 'positive',
      position: 'bottom',
      timeout: 2000
    })
    hasBeenOffline = false
  }
}

const closePromoBanner = () => {
  promoBanner.value.active = false
}

onMounted(async () => {
  isOffline.value = !navigator.onLine

  try {
    await authStore.checkAuth()
  } catch (err) {
    console.error('Auth check failed:', err)
  }

  window.addEventListener('online', onOnline)
  window.addEventListener('offline', onOffline)
})

onUnmounted(() => {
  window.removeEventListener('online', onOnline)
  window.removeEventListener('offline', onOffline)
})
</script>

<template>
  <div class="fixed-promo-banner-container" v-if="!isAdminRoute && isBannerActive">
    <promo-header-banner
      :banner-data="promoBanner"
      @close="closePromoBanner"
    />
  </div>

  <q-layout view="lHh Lpr lFf">
    <app-header-quasar
      v-if="!isAdminRoute"
      @toggle-sidebar="toggleLeftDrawer"
      :class="{
        'header-mobile-home': isHomePage && isMobile.value,
        'header-with-banner': isBannerActive
      }"
    />

    <q-drawer
      v-model="leftDrawerOpen"
      bordered
      :width="280"
      :breakpoint="768"
      class="bg-white"
      :behavior="isMobile ? 'mobile' : 'desktop'"
    >
      <q-list padding class="menu-list">
        <q-item clickable v-ripple :to="{ path: '/' }" exact>
          <q-item-section avatar><q-icon name="home" /></q-item-section>
          <q-item-section>Home</q-item-section>
        </q-item>
        <q-item clickable v-ripple :to="{ path: '/categories' }">
          <q-item-section avatar><q-icon name="category" /></q-item-section>
          <q-item-section>Categories</q-item-section>
        </q-item>
        <q-item clickable v-ripple :to="{ path: '/stores' }">
          <q-item-section avatar><q-icon name="store" /></q-item-section>
          <q-item-section>Stores</q-item-section>
        </q-item>
        <q-item clickable v-ripple :to="{ path: '/deals' }">
          <q-item-section avatar><q-icon name="local_offer" /></q-item-section>
          <q-item-section>Top Deals</q-item-section>
        </q-item>
        <q-separator class="q-my-sm" />
        <q-item clickable v-ripple :to="{ path: '/about' }">
          <q-item-section avatar><q-icon name="info" /></q-item-section>
          <q-item-section>About</q-item-section>
        </q-item>
        <q-item clickable v-ripple :to="{ path: '/contact' }">
          <q-item-section avatar><q-icon name="mail" /></q-item-section>
          <q-item-section>Contact Us</q-item-section>
        </q-item>
      </q-list>
      <div class="drawer-footer absolute-bottom q-pa-md">
        <div class="text-caption text-grey text-center">© 2026 CouponLa India</div>
      </div>
    </q-drawer>

    <q-page-container class="bg-grey-1" :class="{ 'page-with-banner': isBannerActive }">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" :key="route.fullPath" />
        </transition>
      </router-view>
    </q-page-container>

    <app-footer-quasar v-if="!isMobile && !isAdminRoute" />
    <mobile-navigation v-if="isMobile && !isAdminRoute" />
  </q-layout>
</template>

<style lang="scss">
.q-layout {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

a {
  color: inherit;
  text-decoration: none;
}

.material-icons {
  font-family: 'Material Icons';
}

@mixin skeleton-loading {
  position: relative;
  background-color: rgba(0, 0, 0, 0.08);
  border-radius: $generic-border-radius;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0; right: 0; bottom: 0; left: 0;
    background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0) 100%);
    animation: skeletonLoading 1.5s infinite;
  }
}

.skeleton-loader {
  @include skeleton-loading;
}

@keyframes skeletonLoading {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.q-container {
  width: 100%;
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 12px;
  padding-right: 12px;
}

.fixed-promo-banner-container {
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 5000;
  height: 36px;

  @media (max-width: 599px) {
    height: 40px;
  }
}

.header-with-banner {
  top: 36px !important;

  @media (max-width: 599px) {
    top: 40px !important;
  }
}

// Card hover only on desktop — prevents janky tap behavior on mobile
@media (hover: hover) and (pointer: fine) {
  .q-card {
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
    }
  }
}

.header-mobile-home {
  .search-dialog {
    background: linear-gradient(135deg, #3498db 0%, #1976d2 100%);
    padding-top: env(safe-area-inset-top);
    color: white;
  }
}

.q-page-container {
  padding-bottom: 72px;

  @media (min-width: 1024px) {
    padding-bottom: 0;
  }
}

.page-with-banner {
  transition: padding-top 0.3s ease;
}

.custom-shadow {
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.07);
}

.custom-radius {
  border-radius: 12px;
}

.menu-list {
  .q-item {
    border-radius: 8px;
    margin: 2px 8px;
    min-height: 44px;

    &.q-router-link-active {
      background: rgba(var(--q-primary), 0.1);
      color: var(--q-primary);
      font-weight: 500;

      .q-icon {
        color: var(--q-primary);
      }
    }
  }

  .q-icon {
    font-size: 1.2rem;
  }
}

.drawer-footer {
  border-top: 1px solid rgba(0,0,0,0.08);
  background-color: rgba(0,0,0,0.02);
}
</style>
